import { useEffect } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useParams,
} from "react-router-dom";
import { STEPS, type Step } from "@hearing/shared";
import { ScreenerProvider, useScreener } from "./state.js";
import { startAutoResize } from "./embed.js";
import { Layout } from "./components/Layout.js";
import { Welcome } from "./screens/Welcome.js";
import { Details } from "./screens/Details.js";
import { Headphones } from "./screens/Headphones.js";
import { SetVolume } from "./screens/SetVolume.js";
import { Measurement } from "./screens/Measurement.js";
import { Questions } from "./screens/Questions.js";
import { Results } from "./screens/Results.js";

const SCREENS: Record<Step, () => JSX.Element | null> = {
  welcome: Welcome,
  details: Details,
  headphones: Headphones,
  "set-volume": SetVolume,
  test: Measurement,
  questions: Questions,
  results: Results,
};

function isStep(value: string | undefined): value is Step {
  return !!value && (STEPS as readonly string[]).includes(value);
}

function ScreenHost() {
  const { step } = useParams<{ step: string }>();
  const { loading, error } = useScreener();

  if (loading) {
    return (
      <Layout>
        <div className="loading-state" role="status">
          <span className="saving-spinner" aria-hidden />
          <h2>Loading your hearing check</h2>
          <p className="muted">Getting everything ready…</p>
        </div>
      </Layout>
    );
  }
  if (error) {
    return (
      <Layout>
        <div className="empty-state">
          <h2>Something went wrong</h2>
          <p className="muted screen-copy">{error}</p>
        </div>
      </Layout>
    );
  }
  if (!isStep(step)) {
    return <Navigate to={`/welcome${window.location.search}`} replace />;
  }
  const Screen = SCREENS[step];
  return <Screen />;
}

export function App() {
  useEffect(() => {
    startAutoResize();
  }, []);
  return (
    <BrowserRouter>
      <ScreenerProvider>
        <Routes>
          <Route path="/:step" element={<ScreenHost />} />
          <Route
            path="*"
            element={<Navigate to={`/welcome${window.location.search}`} replace />}
          />
        </Routes>
      </ScreenerProvider>
    </BrowserRouter>
  );
}
